/**
 * The deploying view's table, as data. Pure and React-free so the rules that decide
 * what an operator is looking at — is this component done, is anything running on it,
 * is it safe to deploy now — are unit-tested rather than read off a rendered page.
 *
 * Four sources, and which one wins matters:
 *
 *  1. **The deploy marker** (`components/{id}.deployMarker.current`) decides whether
 *     a component is deployed for *this* release. Not `lastDeployed`: the marker is
 *     what a resumed release skips on, and it can be present for a deploy this system
 *     has no record of — one the laptop release tool ran, carried over from its
 *     `deploy.log`. That deploy really happened, and this table must not offer to do
 *     it again. `lastDeployed` is attribution, shown beside it, and absent for
 *     anything this system did not run.
 *  2. **The plan** says what the release changes, so a component with nothing in it
 *     is reported as needing nothing rather than as outstanding work.
 *  3. **Acknowledgements** settle a changed component the operator decided to skip.
 *  4. **This release's instructions** say what is happening right now, which no
 *     document does: a compile takes minutes and writes nothing until it finishes.
 */
import {
    DEPLOY_GROUP_RATIONALE,
    INSTRUCTION_KIND,
    TRIGGERED_MARKER,
    allComponents,
    componentBuildSlots,
    deployKindOf,
    isInflightStatus,
    needsDeploy,
    type Acknowledgement,
    type ChangeKind,
    type ComponentDoc,
    type ComponentImpact,
    type ComponentPlanEntry,
    type DeployKind,
    type DeployRecord,
    type HostMachine,
    type MarkerRecord,
} from '@finvari/deployer';
import type { InstructionRow } from './instructionActivity';

/**
 * What each deploy kind is called on the page.
 *
 * `firebase` is what almost everything is, so the table shows the label only for
 * the other kinds — a caption under every second row saying "Firebase" is noise.
 * The detail panel names it either way, because there it answers a question the
 * operator asked. "(Docker)" is in the label deliberately: it is the difference
 * that explains why these deploys take much longer and why `host.doctor` checks
 * the Docker daemon.
 */
export const DEPLOY_KIND_LABEL: Record<DeployKind, string> = {
    firebase: 'Firebase',
    predictor: 'Cloud Run predictor (Docker)',
    enrichment: 'Cloud Run enrichment (Docker)',
};

/**
 * Where a component stands, as one word for the status column.
 *
 *  - `deployed` — the deploy marker names this release's commit, and this release
 *    put it there.
 *  - `upToDate` — the marker is current but the release changes nothing here, so
 *    production already runs this code and no deploy of this release wrote it.
 *  - `acknowledged` — changed, deliberately skipped, with a reason on the record.
 *  - `outstanding` — the release changes it and neither of the above is true.
 *  - `compileOnly` — a dependency forces a rebuild, but nothing it ships can reach
 *    a changed file at runtime. Distinct from `outstanding` because it is not work
 *    the release is waiting on, and distinct from `unchanged` because it still has
 *    to compile: that compile is the only cross-package type check several of these
 *    modules get.
 *  - `unchanged` — the release does not touch it, so it needs nothing.
 */
export type ComponentState = 'deployed' | 'upToDate' | 'acknowledged' | 'outstanding' | 'compileOnly' | 'unchanged';

/** What is happening to a component right now, from this release's instructions. */
export type ComponentActivity = 'compiling' | 'deploying' | 'failed' | 'queued';

export interface ComponentRow {
    name: string;
    path: string;
    deployGroup: number;
    deployKind: DeployKind;
    compilable: boolean;
    /** The Firebase/GCP project this deploys to, when the registry names one. */
    expectedProject?: string;
    hostingSite?: string;

    changeKind: ChangeKind;
    /** Files the release changes in this component (or via a shared dependency). */
    files: string[];
    /**
     * The runtime verdict behind `changeKind`, when the plan carries one — the
     * reason chains the detail panel shows, and the evidence for a `compileOnly`
     * row claiming no deploy is needed.
     */
    impact?: ComponentImpact;
    state: ComponentState;
    /** True when the compile marker names this release's commit, so a deploy skips it. */
    compiled: boolean;
    deployMarker?: MarkerRecord;
    /**
     * The last deploy this system ran, of any release — the table's "last deployed"
     * column, and the prompt for a no-change redeploy of something nothing has
     * touched in months.
     */
    lastDeployed?: DeployRecord;
    /** Set only when *this system* ran the deploy for this release. */
    deployedBy?: DeployRecord;
    acknowledgement?: Acknowledgement;

    activity?: ComponentActivity;
    /** The instruction behind `activity`, for the log link. */
    instructionId?: string;
}

export interface ComponentRowsInput {
    /** The release the table is about, for attributing `lastDeployed` to it. */
    version: string;
    /** The release plan; absent before the first sync of the boot. */
    plan: Record<string, ComponentPlanEntry> | undefined;
    components: Array<ComponentDoc & { id: string }>;
    acknowledgements: Record<string, Acknowledgement> | undefined;
    /** This release's instructions on the release host, newest first. */
    instructions: InstructionRow[];
}

/** The registry's deploy order: group ascending, then name, as the table shows it. */
function byGroupThenName(a: { deployGroup: number; name: string }, b: { deployGroup: number; name: string }): number {
    return a.deployGroup - b.deployGroup || a.name.localeCompare(b.name);
}

/**
 * One row per registry component, in deploy order.
 *
 * Driven by the registry rather than by the documents: a component the release host
 * has never written a document for still has to appear, or a fresh `(default)`
 * database would show an empty release. Only the parts that describe *this* release
 * come from Firestore.
 */
export function componentRows(input: ComponentRowsInput): ComponentRow[] {
    const docs = new Map(input.components.map((c) => [c.name, c]));
    const activity = activityByComponent(input.instructions);

    return allComponents()
        .sort(byGroupThenName)
        .map((mod) => {
            const doc = docs.get(mod.name);
            const entry = input.plan?.[mod.name];
            const changeKind = entry?.changeKind ?? 'none';
            const acknowledgement = input.acknowledgements?.[mod.name];
            const deployed = doc?.deployMarker?.current === true;
            // Only for this release: `lastDeployed` outlives it, and attributing the
            // previous release's deploy to this one would put a name and a time on a
            // row that nothing has done yet. Matched on version rather than commit
            // because the record carries the full sha and the marker the short one.
            const deployedBy = doc?.lastDeployed?.version === input.version ? doc.lastDeployed : undefined;
            const act = activity.get(mod.name);

            return {
                name: mod.name,
                path: mod.path,
                deployGroup: mod.deployGroup,
                deployKind: deployKindOf(mod),
                compilable: mod.compilable,
                expectedProject: doc?.expectedProject,
                hostingSite: doc?.hostingSite,
                changeKind,
                files: entry?.files ?? [],
                impact: entry?.impact,
                state: stateOf(changeKind, deployed, !!acknowledgement, !!deployedBy),
                compiled: doc?.compileMarker?.current === true,
                deployMarker: doc?.deployMarker ?? undefined,
                lastDeployed: doc?.lastDeployed,
                deployedBy,
                acknowledgement,
                activity: act?.activity,
                instructionId: act?.instructionId,
            };
        });
}

/**
 * A current marker on a component the release changes nothing in was written by an
 * earlier release: production runs the right code, but this release did not put it
 * there, so the row reads `up to date` rather than claiming a deploy as its own. A
 * redeploy the operator asks for anyway is a deploy of this release's code, and
 * `deployedThisRelease` then makes the row plain `deployed`.
 */
function stateOf(
    changeKind: ChangeKind,
    deployed: boolean,
    acknowledged: boolean,
    deployedThisRelease: boolean,
): ComponentState {
    if (deployed) return changeKind === 'none' && !deployedThisRelease ? 'upToDate' : 'deployed';
    if (changeKind === 'none') return 'unchanged';
    // Before the acknowledgement check: a compile-only component does not block the
    // close, so `deploy.acknowledgeSkip` refuses it and there is nothing to show.
    if (changeKind === 'compile-only') return 'compileOnly';
    return acknowledged ? 'acknowledged' : 'outstanding';
}

/** The component an instruction is about, for the two kinds that name one. */
function componentOf(row: InstructionRow): string | undefined {
    const component = (row.params as { component?: unknown } | undefined)?.component;
    return typeof component === 'string' ? component : undefined;
}

/**
 * What each component's newest instruction says is happening to it.
 *
 * Newest-first input, and only the newest per component counts: a failed deploy
 * followed by a successful retry is not a failure, and a component with a compile
 * queued behind a deploy is described by the deploy. A finished success reports
 * nothing — the marker already says so, and the row would otherwise carry two
 * spellings of the same fact.
 */
function activityByComponent(rows: InstructionRow[]): Map<string, { activity: ComponentActivity; instructionId: string }> {
    const out = new Map<string, { activity: ComponentActivity; instructionId: string }>();
    // Tracked apart from `out` because the newest instruction for a component often
    // has nothing to report, and that silence is the answer — keying on `out` would
    // let the failure it superseded through.
    const seen = new Set<string>();
    for (const row of rows) {
        if (row.kind !== INSTRUCTION_KIND.deployCompile && row.kind !== INSTRUCTION_KIND.deployComponent) continue;
        const name = componentOf(row);
        if (!name || seen.has(name)) continue;
        seen.add(name);
        const activity = activityOf(row);
        if (activity) out.set(name, { activity, instructionId: row.id });
    }
    return out;
}

function activityOf(row: InstructionRow): ComponentActivity | undefined {
    if (row.status === 'pending') return 'queued';
    if (row.status === 'running') return row.kind === INSTRUCTION_KIND.deployCompile ? 'compiling' : 'deploying';
    // Rejected counts as failed here: to the operator looking at the row, the
    // difference is why it did not happen, which the log link answers.
    if (row.status === 'failed' || row.status === 'rejected') return 'failed';
    return undefined;
}

/** True while an instruction of either deploy kind is in flight for this component. */
export function isBusy(row: ComponentRow): boolean {
    return row.activity === 'compiling' || row.activity === 'deploying' || row.activity === 'queued';
}

/**
 * Why no further component may be started right now, or `undefined` while the
 * machine has room.
 *
 * The host enforces this too and its answer is the one that counts — this is
 * here so the operator is not invited to press a button that will come back
 * rejected. Queued rows count against the limit because the host dispatches an
 * instruction as soon as it claims it: a queue is not a waiting room.
 *
 * With no `machine` — a host that has not beaten yet, or a controller too old to
 * report one — nothing is capped here. The host still caps it.
 */
export function componentSlotsReason(rows: ComponentRow[], machine: HostMachine | undefined): string | undefined {
    if (!machine) return undefined;
    const slots = componentBuildSlots(machine.memoryGib);
    const busy = rows.filter(isBusy).length;
    if (busy < slots) return undefined;
    const where = [machine.machineType, `${machine.memoryGib} GiB`].filter(Boolean).join(', ');
    const many = busy === 1 ? 'component is' : 'components are';
    return `${busy} ${many} already compiling or deploying, which is the most this machine (${where}) runs at once. Wait for one to finish.`;
}

/** The status column: one phrase and the colour that carries it. */
export interface StateChip {
    label: string;
    color: 'default' | 'info' | 'success' | 'warning' | 'error';
}

/**
 * What the status column says about a component.
 *
 * Activity wins over state, because a component that is being deployed right now is
 * more urgent news than the marker it still carries from before — including a
 * *failed* attempt on something already deployed, which is how a retry that broke
 * shows up. The wording is the spec's (`component-deployment`, Component Table).
 */
export function stateChip(row: ComponentRow): StateChip {
    switch (row.activity) {
        case 'queued':
            return { label: 'queued', color: 'info' };
        case 'compiling':
            return { label: 'compiling', color: 'info' };
        case 'deploying':
            return { label: 'deploying', color: 'info' };
        case 'failed':
            return { label: 'failed', color: 'error' };
    }
    switch (row.state) {
        case 'deployed':
            return { label: 'deployed', color: 'success' };
        // Grey rather than green: like `no changes`, this is the state the component
        // was already in, not something this release achieved.
        case 'upToDate':
            return { label: 'up to date', color: 'default' };
        // "skipped" is what the operator did; `acknowledged` is what the release doc
        // stores and what the instruction is called. The state key follows the record,
        // the label follows the page.
        case 'acknowledged':
            return { label: 'skipped', color: 'warning' };
        case 'unchanged':
            return { label: 'no changes', color: 'default' };
        // Not a warning colour: there is nothing here for the operator to resolve.
        // The row exists so the compile is visible, and reads as settled.
        case 'compileOnly':
            return { label: 'compile only', color: 'default' };
        case 'outstanding':
            return { label: 'not started', color: 'warning' };
    }
}

/**
 * How the change column describes a component's changes. A dependency-only change
 * carries the registry's marker instead of a file list, so a count would read "1
 * file" for something with no files of its own.
 */
export function changeSummary(row: ComponentRow): string {
    if (row.changeKind === 'none') return 'none';
    if (row.files.length === 1 && row.files[0] === TRIGGERED_MARKER) {
        // The claim, not just the trigger: this row says "no deploy needed", and the
        // column is where an operator scanning the table learns why.
        return row.changeKind === 'compile-only' ? 'shared dependency (no runtime path)' : 'shared dependency';
    }
    const files = `${row.files.length} ${row.files.length === 1 ? 'file' : 'files'}`;
    return row.changeKind === 'dependency' ? `${files} (shared dependency)` : files;
}

/**
 * The one-line justification for a row that is not asking to be deployed.
 *
 * Phrased as what was checked rather than as a verdict, because the operator is
 * being asked to trust a static analysis: the honest form names the bound (the
 * component's own compiled files reach none of the changed ones) rather than
 * asserting that nothing can go wrong.
 */
export function compileOnlyExplanation(row: ComponentRow): string {
    return (
        `${row.name} rebuilds because a package it depends on changed, but no file it ships reaches a ` +
        'changed file through an import that carries a value. Compile it; deploy it only if you want ' +
        'the extra certainty.'
    );
}

export interface OrderWarning {
    /** The outstanding component in the lowest group ahead of this one. */
    component: string;
    group: number;
    /** Why that group deploys first — the registry's own reason. */
    reason: string;
}

/**
 * The soft warning for deploying `row` before an outstanding component in a lower
 * group. Advice, not a block (registry `deployGroup`): the operator may have a good
 * reason — a hotfix to one service, or a component whose deploy just failed — and the
 * groups only encode runtime call dependencies. What the warning must do is name the
 * component that ought to go first and why, so going ahead is a decision rather than
 * an oversight.
 */
export function orderWarning(row: ComponentRow, rows: ComponentRow[]): OrderWarning | undefined {
    const ahead = rows
        .filter((r) => r.state === 'outstanding' && r.deployGroup < row.deployGroup)
        .sort(byGroupThenName)[0];
    if (!ahead) return undefined;
    return {
        component: ahead.name,
        group: ahead.deployGroup,
        reason: DEPLOY_GROUP_RATIONALE[ahead.deployGroup] ?? '',
    };
}

export interface DeployProgress {
    /** Components the release has a reason to deploy — the denominator of the work. */
    changed: number;
    deployed: number;
    acknowledged: number;
    outstanding: number;
    /** Deployed even though the release changes nothing in them (a deliberate redeploy). */
    redeployed: number;
    /** In the release for the compile, with no runtime path to a changed file. */
    compileOnly: number;
}

/**
 * The counts the header badge and the close card read. Over the components the
 * release has a *reason to deploy*, because "5 of 9" is only meaningful about the
 * work the release created; a redeploy of something it does not need is counted
 * separately rather than inflating either side.
 *
 * `compile-only` is one of those: it is in the release because falc rebuilds it,
 * and counting it as work would put the operator back where this started — a
 * denominator of every module in the repo on every `model` change.
 */
export function deployProgress(rows: ComponentRow[]): DeployProgress {
    const changed = rows.filter((r) => needsDeploy(r.changeKind));
    return {
        changed: changed.length,
        deployed: changed.filter((r) => r.state === 'deployed').length,
        acknowledged: changed.filter((r) => r.state === 'acknowledged').length,
        outstanding: changed.filter((r) => r.state === 'outstanding').length,
        redeployed: rows.filter((r) => !needsDeploy(r.changeKind) && r.state === 'deployed').length,
        compileOnly: rows.filter((r) => r.changeKind === 'compile-only').length,
    };
}

/** True when every changed component is deployed or acknowledged — the close gate. */
export function allSettled(rows: ComponentRow[]): boolean {
    return deployProgress(rows).outstanding === 0;
}

/**
 * Whether an instruction row belongs to the deploy activity of a component. Used by
 * the detail panel's Log tab to find the newest run for one component, including the
 * finished ones `activityByComponent` deliberately drops.
 */
export function instructionsFor(name: string, rows: InstructionRow[]): InstructionRow[] {
    return rows.filter(
        (r) =>
            (r.kind === INSTRUCTION_KIND.deployCompile ||
                r.kind === INSTRUCTION_KIND.deployComponent ||
                r.kind === INSTRUCTION_KIND.deployAcknowledgeSkip ||
                r.kind === INSTRUCTION_KIND.deployCancelSkip) &&
            componentOf(r) === name,
    );
}

/** The newest instruction for one component, and whether it is still running. */
export function latestFor(name: string, rows: InstructionRow[]): { row: InstructionRow; live: boolean } | undefined {
    const row = instructionsFor(name, rows)[0];
    return row ? { row, live: isInflightStatus(row.status) } : undefined;
}
